import { ArrowRight, Linkedin, Mail } from 'lucide-react';
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from 'motion/react';
import { useRef } from 'react';

import { PastLedger } from './events.js';
import { pastEvents, upcomingEvents } from './lib/events-data.js';
import {
  Container,
  GrainOverlay,
  ScrollReveal,
  SectionHeader,
} from './lib/impeccable.js';
import { Footer, Header, JOIN_URL } from './lib/site.js';
import { BlockAnimation } from './routes/block-animation.js';
import { FlaskAnimation } from './routes/hero-animations.js';

const whatWeDo = [
  {
    num: '01',
    title: 'Knowledge Exchange',
    description:
      "Deep-dive sessions, talks, and discussions on what's actually working in AI engineering today — from people who are building, not just reading about it.",
  },
  {
    num: '02',
    title: 'Networking',
    description:
      'Connect with AI engineers across Jordan who are building seriously — at startups, companies, and on real projects.',
  },
  {
    num: '03',
    title: 'Events',
    description:
      'Regular in-person meetups and hands-on sessions in Jordan. No fluff, no intro-to-AI content — just substantive conversations and real engineering work.',
  },
];

const faqs = [
  {
    q: 'Who is this community for?',
    a: 'AI engineers in Jordan who are actively building with AI — at a company, a startup, or on serious projects.',
  },
  {
    q: 'Do I need to be an expert?',
    a: 'You should have hands-on experience building with AI. This community is not aimed at beginners or those casually exploring the space.',
  },
  {
    q: 'Where and how often do you meet?',
    a: 'We meet biweekly in Amman. Follow us to get notified about upcoming events.',
  },
  {
    q: 'Is this only in Amman?',
    a: 'Our events are currently in Amman, but the community is open to anyone in Jordan.',
  },
  {
    q: 'Is it free?',
    a: 'Yes. Events are free to attend.',
  },
  {
    q: 'How do I join?',
    a: 'Hit the Join the Community button to join our WhatsApp group.',
  },
];

const organizers = [
  {
    name: 'Ezz Abuzaid',
    title: 'AI Engineer',
    company: 'limerence.sh',
    companyUrl: 'https://limerence.sh/',
    bio: 'AI Engineer. Dev Writer. Community Builder.<br /> I build AI engineering tools in TypeScript and write articles when Claude is writing code.',
    linkedin: 'https://linkedin.com/in/ezzabuzaid',
    email: 'ezzabuzaid@gmail.com',
  },
  {
    name: 'Farah Abu Hmaidan',
    title: 'Software Engineer',
    company: 'Simplify9',
    companyUrl: 'https://simplify9.com',
    bio: 'Software engineer focused on AI-powered products. Community builder.',
    linkedin: 'https://linkedin.com/in/farahabuhmaidan',
    email: 'abuhmaidan.farah@gmail.com',
  },
];

function Hero() {
  return (
    <section className="flex min-h-dvh flex-col justify-center pt-32 pb-20">
      <Container>
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <h1 className="font-heading text-foreground mb-6 text-[clamp(2.5rem,5vw,4.5rem)] leading-[1.15] font-light tracking-[-0.02em] text-balance sm:leading-18">
              a space for{' '}
              <em className="text-primary font-normal not-italic">
                ai engineers
              </em>{' '}
              in Jordan
            </h1>
            <p className="text-secondary-foreground mb-8 max-w-[55ch] text-lg leading-relaxed text-pretty">
              A space for AI engineers to exchange knowledge, share what they're
              building, and connect with others who are serious about AI.
            </p>

            <div className="mb-12 grid grid-cols-2 gap-4 sm:flex sm:flex-wrap sm:items-center">
              <a
                href={JOIN_URL}
                className="bg-primary before:bg-[#d44400] relative inline-flex w-full items-center justify-center gap-2 overflow-hidden px-4 py-3.5 text-sm font-semibold tracking-[0.02em] text-white shadow-sm transition-all before:absolute before:inset-0 before:translate-y-full before:transition-transform hover:-translate-y-0.5 hover:shadow-md hover:before:translate-y-0 sm:w-auto sm:px-7 rounded-sm"
              >
                <span className="relative z-10 inline-flex items-center gap-2">
                  Join the Community
                  <ArrowRight size={16} />
                </span>
              </a>
            </div>
          </div>

          <div className="hidden lg:block">
            <FlaskAnimation />
            <BlockAnimation />
          </div>
        </div>
      </Container>
    </section>
  );
}

function WhatWeDoSection() {
  return (
    <section className="py-20" id="community">
      <Container>
        <ScrollReveal>
          <SectionHeader
            number="01"
            title="What We Do"
            subtitle="We bring AI engineers in Jordan together — in person and online."
          />
        </ScrollReveal>
        <div className="mt-12">
          {whatWeDo.map((item) => (
            <ScrollReveal key={item.num}>
              <div className="border-border grid items-start gap-8 border-t py-12 md:grid-cols-[80px_1fr]">
                <span className="text-primary font-mono text-sm font-medium">
                  {item.num}
                </span>
                <div>
                  <h3 className="font-heading text-foreground mb-4 text-[clamp(1.5rem,3vw,2rem)] font-light">
                    {item.title}
                  </h3>
                  <p className="text-secondary-foreground text-sm leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

function EventsSection() {
  return (
    <section className="py-20" id="events">
      <Container>
        <ScrollReveal>
          <SectionHeader
            number="02"
            title="Events"
            subtitle="Biweekly, in person, in Amman. Talks, hackathons, workshops, Build Coffee, and networking events — free to attend."
          />
        </ScrollReveal>
        <ScrollReveal className="mt-12">
          {upcomingEvents.length > 0 && (
            <div className="mb-10">
              <span className="text-primary font-mono text-[0.625rem] font-medium tracking-[0.15em] uppercase">
                Next up
              </span>
              <p className="font-heading text-foreground mt-2 text-[clamp(1.25rem,2.5vw,1.75rem)] font-light">
                {upcomingEvents.map((event) => event.title).join('  ·  ')}
              </p>
            </div>
          )}
          <PastLedger events={pastEvents.slice(0, 3)} />
          <a
            href="/events"
            className="text-primary mt-8 inline-flex items-center gap-2 font-mono text-sm tracking-wider uppercase no-underline hover:underline"
          >
            See all events
            <ArrowRight size={14} />
          </a>
        </ScrollReveal>
      </Container>
    </section>
  );
}

function AboutSection() {
  return (
    <section className="py-20" id="about">
      <Container>
        <ScrollReveal>
          <SectionHeader
            number="03"
            title="The Community"
            subtitle="Engineers who build with AI, share what they learn, and show up for each other."
          />
        </ScrollReveal>
        <ScrollReveal>
          <div className="mt-12 grid gap-8 sm:grid-cols-3">
            <div className="border-border border-t pt-6">
              <span className="text-muted-foreground font-mono text-xs tracking-wider uppercase">
                Who
              </span>
              <p className="text-foreground mt-2 text-sm leading-relaxed">
                AI engineers working seriously with AI.
              </p>
            </div>
            <div className="border-border border-t pt-6">
              <span className="text-muted-foreground font-mono text-xs tracking-wider uppercase">
                Where
              </span>
              <p className="text-foreground mt-2 text-sm leading-relaxed">
                Based in Jordan. Events currently held in Amman.
              </p>
            </div>
            <div className="border-border border-t pt-6">
              <span className="text-muted-foreground font-mono text-xs tracking-wider uppercase">
                What
              </span>
              <p className="text-foreground mt-2 text-sm leading-relaxed">
                Knowledge exchange and networking — not beginner content or
                casual exploration.
              </p>
            </div>
          </div>
        </ScrollReveal>
      </Container>
    </section>
  );
}

function TeamSection() {
  return (
    <section className="py-20">
      <Container>
        <ScrollReveal>
          <SectionHeader number="04" title="Organizers" />
        </ScrollReveal>
        <ScrollReveal>
          <div className="mt-12 grid gap-8 sm:grid-cols-2">
            {organizers.map((organizer) => (
              <div key={organizer.name}>
                <a
                  href={organizer.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground font-mono text-sm font-semibold tracking-wider uppercase hover:underline"
                >
                  {organizer.name}
                </a>
                <p className="mt-1 text-xs">
                  <span className="text-muted-foreground">
                    {organizer.title} @{' '}
                  </span>
                  <a
                    href={organizer.companyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {organizer.company}
                  </a>
                </p>
                <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
                  <span dangerouslySetInnerHTML={{ __html: organizer.bio }} />
                </p>
                <div className="mt-3 flex items-center gap-4">
                  <a
                    href={organizer.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary inline-flex items-center gap-1.5 font-mono text-xs font-medium hover:underline"
                  >
                    <Linkedin size={14} />
                    LinkedIn
                  </a>
                  <a
                    href={`mailto:${organizer.email}`}
                    className="text-primary inline-flex items-center gap-1.5 font-mono text-xs font-medium hover:underline"
                  >
                    <Mail size={14} />
                    Contact
                  </a>
                </div>
              </div>
            ))}
          </div>
        </ScrollReveal>
      </Container>
    </section>
  );
}

function FAQSection() {
  return (
    <section className="py-20" id="faq">
      <Container>
        <ScrollReveal>
          <SectionHeader number="05" title="FAQ" />
        </ScrollReveal>
        <ScrollReveal>
          <div className="mx-auto max-w-3xl">
            {faqs.map((faq) => (
              <details key={faq.q} className="group border-border border-b">
                <summary className="text-foreground flex cursor-pointer items-center justify-between py-6 [&::-webkit-details-marker]:hidden [&::marker]:hidden">
                  <span className="font-heading pr-4 text-lg font-normal">
                    {faq.q}
                  </span>
                  <span className="text-muted-foreground shrink-0 transition-transform group-open:rotate-45">
                    +
                  </span>
                </summary>
                <div className="text-secondary-foreground pb-6 text-sm leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </ScrollReveal>
      </Container>
    </section>
  );
}

function PartnerSection() {
  return (
    <section className="py-20">
      <Container>
        <ScrollReveal>
          <SectionHeader
            number="06"
            title="Partner With Us"
            subtitle="Support a community of engineers who are serious about AI."
          />
        </ScrollReveal>
        <ScrollReveal>
          <div className="mt-12 grid gap-12 lg:grid-cols-2">
            <div>
              <p className="text-secondary-foreground text-sm leading-relaxed">
                We're open to partnerships with companies, startups, and
                organizations who want to engage with AI engineers in Jordan —
                whether that's sponsoring an event, hosting a session, or
                collaborating on something new.
              </p>
              <a
                href="mailto:ezzabuzaid@gmail.com,abuhmaidan.farah@gmail.com?subject=Partnership%20Inquiry%20%E2%80%94%20The%20Agent%20Lab&body=Hi%2C%0A%0AI%27m%20interested%20in%20partnering%20with%20The%20Agent%20Lab.%0A%0A"
                className="bg-primary hover:bg-[#d44400] mt-6 inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white transition-colors"
              >
                Get in Touch
                <ArrowRight size={16} />
              </a>
            </div>
            <div className="space-y-0">
              {[
                'Visibility with a focused, technical audience',
                'Opportunity to host or co-host events',
                'A genuine connection with the AI engineering community in Jordan',
              ].map((item) => (
                <div
                  key={item}
                  className="border-border flex items-start gap-3 border-t py-5"
                >
                  <span className="text-primary mt-0.5 shrink-0 font-mono text-sm">
                    →
                  </span>
                  <span className="text-secondary-foreground text-sm">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </Container>
    </section>
  );
}

const AGENT_LAB_PATH =
  'M0.48-33.60L0.48-29.76L9.12-29.76L9.12 0L13.20 0L13.20-29.76L21.84-29.76L21.84-33.60L0.48-33.60ZM29.76 0L29.76-37.44L25.92-37.44L25.92 0L29.76 0ZM40.08-13.44L40.08-13.44L40.08 0L43.92 0L43.92-13.92Q43.92-16.80 42.98-18.72Q42.05-20.64 40.34-21.60Q38.64-22.56 36.34-22.56L36.34-22.56Q34.03-22.56 32.28-21.36Q30.53-20.16 29.52-18.12Q28.51-16.08 28.51-13.44L28.51-13.44L29.76-13.44Q29.76-15.02 30.46-16.27Q31.15-17.52 32.42-18.24Q33.70-18.96 35.38-18.96L35.38-18.96Q37.73-18.96 38.90-17.57Q40.08-16.18 40.08-13.44ZM59.66 0.48L59.66 0.48Q63.22 0.48 65.83-0.94Q68.45-2.35 70.18-5.14L70.18-5.14L67.06-7.10Q65.86-5.09 64.13-4.10Q62.40-3.12 60.14-3.12L60.14-3.12Q57.89-3.12 56.30-4.08Q54.72-5.04 53.90-6.86Q53.09-8.69 53.09-11.28L53.09-11.28Q53.14-13.82 53.98-15.58Q54.82-17.33 56.38-18.26Q57.94-19.20 60.14-19.20L60.14-19.20Q62.02-19.20 63.41-18.38Q64.80-17.57 65.59-16.13Q66.38-14.69 66.38-12.72L66.38-12.72Q66.38-12.38 66.24-11.88Q66.10-11.38 65.95-11.14L65.95-11.14L67.49-13.34L51.65-13.34L51.65-10.08L70.51-10.08Q70.51-10.18 70.54-10.54Q70.56-10.90 70.56-11.23L70.56-11.23Q70.56-14.74 69.31-17.28Q68.06-19.82 65.74-21.19Q63.41-22.56 60.14-22.56L60.14-22.56Q56.88-22.56 54.41-21.12Q51.94-19.68 50.57-17.09Q49.20-14.50 49.20-11.04L49.20-11.04Q49.20-7.63 50.52-5.04Q51.84-2.45 54.22-0.98Q56.59 0.48 59.66 0.48ZM95.14-13.92L93.70-10.08L111.46-10.08L110.02-13.92L95.14-13.92ZM96.82-12.67L102.48-26.02L108.24-12.48L108.62-11.52L113.52 0L118.08 0L102.48-35.28L86.88 0L91.44 0L96.43-11.81L96.82-12.67ZM124.32 1.44L120.43 1.44Q120.43 4.27 121.82 6.46Q123.22 8.64 125.69 9.84Q128.16 11.04 131.38 11.04L131.38 11.04Q134.16 11.04 136.66 9.74Q139.15 8.45 140.74 5.88Q142.32 3.31 142.32-0.48L142.32-0.48L142.32-22.08L138.48-22.08L138.48-0.48Q138.48 2.30 137.40 4.06Q136.32 5.81 134.69 6.62Q133.06 7.44 131.38 7.44L131.38 7.44Q129.22 7.44 127.63 6.60Q126.05 5.76 125.18 4.39Q124.32 3.02 124.32 1.44L124.32 1.44ZM120-11.04L120-11.04Q120-7.49 121.49-4.90Q122.98-2.30 125.47-0.91Q127.97 0.48 130.94 0.48L130.94 0.48Q133.73 0.48 135.79-0.91Q137.86-2.30 139.03-4.90Q140.21-7.49 140.21-11.04L140.21-11.04Q140.21-14.64 139.03-17.21Q137.86-19.78 135.79-21.17Q133.73-22.56 130.94-22.56L130.94-22.56Q127.97-22.56 125.47-21.17Q122.98-19.78 121.49-17.21Q120-14.64 120-11.04ZM123.89-11.04L123.89-11.04Q123.89-13.58 124.92-15.36Q125.95-17.14 127.68-18.05Q129.41-18.96 131.42-18.96L131.42-18.96Q132.67-18.96 133.92-18.43Q135.17-17.90 136.20-16.90Q137.23-15.89 137.86-14.42Q138.48-12.96 138.48-11.04L138.48-11.04Q138.48-8.50 137.42-6.72Q136.37-4.94 134.74-4.03Q133.10-3.12 131.42-3.12L131.42-3.12Q129.41-3.12 127.68-4.03Q125.95-4.94 124.92-6.72Q123.89-8.50 123.89-11.04ZM157.82 0.48L157.82 0.48Q161.38 0.48 163.99-0.94Q166.61-2.35 168.34-5.14L168.34-5.14L165.22-7.10Q164.02-5.09 162.29-4.10Q160.56-3.12 158.30-3.12L158.30-3.12Q156.05-3.12 154.46-4.08Q152.88-5.04 152.06-6.86Q151.25-8.69 151.25-11.28L151.25-11.28Q151.30-13.82 152.14-15.58Q152.98-17.33 154.54-18.26Q156.10-19.20 158.30-19.20L158.30-19.20Q160.18-19.20 161.57-18.38Q162.96-17.57 163.75-16.13Q164.54-14.69 164.54-12.72L164.54-12.72Q164.54-12.38 164.40-11.88Q164.26-11.38 164.11-11.14L164.11-11.14L165.65-13.34L149.81-13.34L149.81-10.08L168.67-10.08Q168.67-10.18 168.70-10.54Q168.72-10.90 168.72-11.23L168.72-11.23Q168.72-14.74 167.47-17.28Q166.22-19.82 163.90-21.19Q161.57-22.56 158.30-22.56L158.30-22.56Q155.04-22.56 152.57-21.12Q150.10-19.68 148.73-17.09Q147.36-14.50 147.36-11.04L147.36-11.04Q147.36-7.63 148.68-5.04Q150-2.45 152.38-0.98Q154.75 0.48 157.82 0.48ZM188.16-13.44L188.16-13.44L188.16 0L192 0L192-13.92Q192-17.90 189.96-20.23Q187.92-22.56 184.42-22.56L184.42-22.56Q182.26-22.56 180.60-21.60Q178.94-20.64 177.84-18.62L177.84-18.62L177.84-22.08L174-22.08L174 0L177.84 0L177.84-13.44Q177.84-15.07 178.54-16.32Q179.23-17.57 180.50-18.26Q181.78-18.96 183.46-18.96L183.46-18.96Q185.76-18.96 186.96-17.57Q188.16-16.18 188.16-13.44ZM206.88-22.08L195.84-22.08L195.84-18.48L206.88-18.48L206.88-22.08ZM203.28-29.76L199.44-29.76L199.44 0L203.28 0L203.28-29.76ZM229.44-33.60L225.36-33.60L225.36 0L242.64 0L242.64-3.84L229.44-3.84L229.44-33.60ZM248.45-6.82L248.45-6.82Q248.45-8.02 249.02-8.88Q249.60-9.74 250.78-10.22Q251.95-10.70 253.82-10.70L253.82-10.70Q255.84-10.70 257.62-10.20Q259.39-9.70 261.07-8.54L261.07-8.54L261.07-10.80Q260.74-11.23 259.78-11.93Q258.82-12.62 257.21-13.18Q255.60-13.73 253.20-13.73L253.20-13.73Q249.12-13.73 246.84-11.78Q244.56-9.84 244.56-6.62L244.56-6.62Q244.56-4.37 245.62-2.78Q246.67-1.20 248.42-0.36Q250.18 0.48 252.19 0.48L252.19 0.48Q254.02 0.48 255.86-0.19Q257.71-0.86 258.98-2.26Q260.26-3.65 260.26-5.76L260.26-5.76L259.49-8.64Q259.49-6.91 258.65-5.59Q257.81-4.27 256.39-3.55Q254.98-2.83 253.20-2.83L253.20-2.83Q251.81-2.83 250.73-3.29Q249.65-3.74 249.05-4.66Q248.45-5.57 248.45-6.82ZM246-19.82L247.87-16.99Q248.40-17.38 249.31-17.90Q250.22-18.43 251.54-18.82Q252.86-19.20 254.50-19.20L254.50-19.20Q255.50-19.20 256.42-19.01Q257.33-18.82 258.02-18.38Q258.72-17.95 259.10-17.21Q259.49-16.46 259.49-15.31L259.49-15.31L259.49 0L263.33 0L263.33-15.84Q263.33-18 262.25-19.49Q261.17-20.98 259.22-21.77Q257.28-22.56 254.69-22.56L254.69-22.56Q251.62-22.56 249.43-21.65Q247.25-20.74 246-19.82L246-19.82ZM273.41 0L273.41-37.44L269.57-37.44L269.57 0L273.41 0ZM291.89-11.04L291.89-11.04Q291.89-14.64 290.40-17.21Q288.91-19.78 286.44-21.17Q283.97-22.56 280.94-22.56L280.94-22.56Q278.21-22.56 276.12-21.17Q274.03-19.78 272.86-17.21Q271.68-14.64 271.68-11.04L271.68-11.04Q271.68-7.49 272.86-4.90Q274.03-2.30 276.12-0.91Q278.21 0.48 280.94 0.48L280.94 0.48Q283.97 0.48 286.44-0.91Q288.91-2.30 290.40-4.90Q291.89-7.49 291.89-11.04ZM288-11.04L288-11.04Q288-8.50 286.97-6.72Q285.94-4.94 284.23-4.03Q282.53-3.12 280.46-3.12L280.46-3.12Q278.78-3.12 277.15-4.03Q275.52-4.94 274.46-6.72Q273.41-8.50 273.41-11.04L273.41-11.04Q273.41-13.58 274.46-15.36Q275.52-17.14 277.15-18.05Q278.78-18.96 280.46-18.96L280.46-18.96Q282.53-18.96 284.23-18.05Q285.94-17.14 286.97-15.36Q288-13.58 288-11.04Z';

function AgentLabDraw({
  scrollYProgress,
  reduced,
}: {
  scrollYProgress: ReturnType<typeof useScroll>['scrollYProgress'];
  reduced: boolean | null;
}) {
  const pathLength = useTransform(scrollYProgress, [0.35, 0.7], [0, 1]);
  const fillOpacity = useTransform(scrollYProgress, [0.6, 0.8], [0, 1]);

  return (
    <div className="mb-8 flex justify-center">
      <svg
        viewBox="-2 -40 296 53"
        className="h-[clamp(2rem,4vw,3.5rem)] w-auto"
        fill="none"
      >
        <motion.path
          d={AGENT_LAB_PATH}
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          className="text-foreground"
          style={reduced ? undefined : { pathLength }}
        />
        <motion.path
          d={AGENT_LAB_PATH}
          fill="currentColor"
          className="text-foreground"
          style={reduced ? undefined : { opacity: fillOpacity }}
        />
      </svg>
    </div>
  );
}

function MagneticLink({
  children,
  className,
  href,
}: {
  children: React.ReactNode;
  className?: string;
  href: string;
}) {
  const ref = useRef<HTMLAnchorElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 150, damping: 15 });
  const springY = useSpring(y, { stiffness: 150, damping: 15 });

  const handleMove = (e: React.MouseEvent) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    x.set((e.clientX - rect.left - rect.width / 2) * 0.3);
    y.set((e.clientY - rect.top - rect.height / 2) * 0.3);
  };

  const handleLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.a
      ref={ref}
      href={href}
      className={className}
      style={{ x: springX, y: springY }}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
    >
      {children}
    </motion.a>
  );
}

function CommunityCTA() {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 80%', 'end 40%'],
  });

  const flaskPathLength = useTransform(scrollYProgress, [0, 0.5], [0, 1]);
  const neckPathLength = useTransform(scrollYProgress, [0.1, 0.4], [0, 1]);
  const liquidOpacity = useTransform(scrollYProgress, [0.4, 0.6], [0, 0.15]);
  const terminalOpacity = useTransform(scrollYProgress, [0.5, 0.7], [0, 1]);
  const bubblesPathLength = useTransform(scrollYProgress, [0.3, 0.6], [0, 1]);
  const contentOpacity = useTransform(scrollYProgress, [0.75, 0.9], [0, 1]);
  const contentY = useTransform(scrollYProgress, [0.75, 0.9], [20, 0]);

  return (
    <section ref={ref} className="py-30" id="join">
      <Container>
        <div className="text-center">
          <div className="mb-10 flex justify-center">
            <svg viewBox="0 0 100 100" className="size-48" fill="none">
              <motion.path
                d="M40,10 C36,10 36,12 36,14 L36,34 C20,46 12,62 16,78 C20,90 34,96 50,96 C66,96 80,90 84,78 C88,62 80,46 64,34 L64,14 C64,12 64,10 60,10"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-foreground"
                style={reduced ? undefined : { pathLength: flaskPathLength }}
              />
              <motion.path
                d="M34,10 C34,8 40,7 50,7 C60,7 66,8 66,10"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                className="text-foreground"
                style={reduced ? undefined : { pathLength: neckPathLength }}
              />
              <motion.path
                d="M22,68 C22,58 30,50 50,50 C70,50 78,58 78,68 C80,82 68,94 50,94 C32,94 20,82 22,68 Z"
                fill="#e84a00"
                style={reduced ? undefined : { opacity: liquidOpacity }}
              />
              <motion.text
                x="32"
                y="80"
                fontFamily="'Space Mono', monospace"
                fontSize="20"
                fontWeight="700"
                fill="#e84a00"
                style={reduced ? undefined : { opacity: terminalOpacity }}
              >
                &gt;_
              </motion.text>
              <motion.circle
                cx="56"
                cy="44"
                r="3.5"
                fill="none"
                stroke="#e84a00"
                strokeWidth="1.5"
                style={
                  reduced
                    ? { opacity: 0.8 }
                    : { pathLength: bubblesPathLength, opacity: 0.8 }
                }
              />
              <motion.circle
                cx="46"
                cy="34"
                r="2.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.2"
                className="text-foreground"
                style={
                  reduced
                    ? { opacity: 0.5 }
                    : { pathLength: bubblesPathLength, opacity: 0.5 }
                }
              />
              <motion.circle
                cx="54"
                cy="24"
                r="1.8"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                className="text-foreground"
                style={
                  reduced
                    ? { opacity: 0.35 }
                    : { pathLength: bubblesPathLength, opacity: 0.35 }
                }
              />
            </svg>
          </div>

          <AgentLabDraw scrollYProgress={scrollYProgress} reduced={reduced} />

          <MagneticLink
            href={JOIN_URL}
            className="border-primary text-primary inline-flex items-center gap-2 rounded-md border px-10 py-4 text-base font-medium"
          >
            Join the Community
            <ArrowRight size={16} />
          </MagneticLink>

          <motion.div
            style={
              reduced ? undefined : { opacity: contentOpacity, y: contentY }
            }
          >
            <p className="text-muted-foreground mt-8 font-mono text-sm tracking-wider uppercase">
              Jordan · theagentlab.dev
            </p>
            <p className="text-muted-foreground mt-6 text-sm">
              Join a community of AI engineers in Jordan — exchanging knowledge
              and connecting with people who are serious about AI.
            </p>
          </motion.div>
        </div>
      </Container>
    </section>
  );
}

export default function Community() {
  return (
    <div className="min-h-dvh">
      <GrainOverlay />
      <Header />
      <Hero />
      <WhatWeDoSection />
      <EventsSection />
      <AboutSection />
      <TeamSection />
      <FAQSection />
      <PartnerSection />
      <CommunityCTA />
      <Footer />
    </div>
  );
}
